import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import Home from './Home';
import Team from './Team';
import Contact from './Contact';
import Career from './Career';
import Business from './Business';
import Pipeline from './Pipeline';
import Footer from './Footer';
import { LanguageProvider, useLanguage, LANGUAGES } from './i18n/LanguageContext';

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

function Navigation({ isMobile, onClose, isScrolled }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const handleNavClick = (e, path, hash) => {
    e.preventDefault();
    if (onClose) onClose();

    if (location.pathname !== path) {
      navigate(path + hash);
    } else {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const navItems = [
    { label: t.nav.aboutUs, path: '/', hash: '#about' },
    { label: t.nav.ourBusiness, path: '/business', hash: '' },
    { label: t.nav.ourManagement, path: '/team', hash: '' },
    { label: t.nav.projectPipeline, path: '/pipeline', hash: '' },
    { label: t.nav.career, path: '/career', hash: '' },
    { label: t.nav.contactUs, path: '/contact', hash: '' },
  ];

  const baseClasses = isMobile
    ? "block py-3 text-navy-primary font-medium text-base hover:text-blue-accent transition-all duration-300 border-b border-gray-100 last:border-b-0"
    : `${isScrolled ? 'text-navy-primary' : 'text-white'} font-medium text-xs uppercase tracking-wider hover:text-blue-accent transition-all duration-300 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-accent after:to-gold-accent after:transition-all hover:after:w-full`;

  return (
    <nav className={isMobile ? "flex flex-col" : "flex gap-6"}>
      {navItems.map((item, idx) => (
        item.hash ? (
          <a
            key={idx}
            href={item.path + item.hash}
            onClick={(e) => handleNavClick(e, item.path, item.hash)}
            className={baseClasses}
          >
            {item.label}
          </a>
        ) : item.path ? (
          <Link
            key={idx}
            to={item.path}
            onClick={onClose}
            className={baseClasses}
          >
            {item.label}
          </Link>
        ) : (
          <span
            key={idx}
            className={`${baseClasses} cursor-default opacity-60`}
          >
            {item.label}
          </span>
        )
      ))}
    </nav>
  );
}

function LanguageButton({ isScrolled, isMobileMenuOpen }) {
  const [isOpen, setIsOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  const currentLang = LANGUAGES[language] || LANGUAGES.en;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 border transition-all duration-300 text-sm font-medium ${
          isMobileMenuOpen
            ? 'border-gray-300 text-navy-primary hover:border-blue-accent'
            : isScrolled
              ? 'border-gray-300 text-navy-primary hover:border-blue-accent'
              : 'border-white/30 text-white hover:border-white/60'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
        {language.toUpperCase()}
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-32 bg-white shadow-xl border border-gray-100 py-1 z-50">
          {Object.values(LANGUAGES).map((lang) => (
            <button
              key={lang.code}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                language === lang.code ? 'text-blue-accent font-medium' : 'text-navy-primary'
              }`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <span className="text-xs">{lang.flag}</span> {lang.label}
              {language === lang.code && (
                <svg className="w-4 h-4 ml-auto text-blue-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== PASSWORD PROTECTION ==========
// To disable: set ENABLED to false or delete this component
const PASSWORD_CONFIG = {
  ENABLED: false,
  PASSWORD: 'dustinstudio',  // Change this to your desired password
  STORAGE_KEY: 'ventunext_access'
};

function PasswordGate({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(PASSWORD_CONFIG.STORAGE_KEY);
    if (stored === 'granted') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === PASSWORD_CONFIG.PASSWORD) {
      localStorage.setItem(PASSWORD_CONFIG.STORAGE_KEY, 'granted');
      setIsAuthenticated(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  if (!PASSWORD_CONFIG.ENABLED || isAuthenticated) {
    return children;
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-navy-dark via-navy-primary to-navy-light flex items-center justify-center z-[9999]">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-accent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gold-accent rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative bg-white/10 backdrop-blur-xl p-10 max-w-md w-full mx-4 border border-white/20 shadow-2xl">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="VentuNext" className="h-8 mx-auto mb-6 brightness-0 invert" />
          <h2 className="text-2xl font-light text-white mb-2">Preview Access</h2>
          <p className="text-white/60 text-sm">Enter password to continue</p>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className={`w-full px-4 py-3 bg-white/10 border-2 ${error ? 'border-red-400' : 'border-white/20'} text-white placeholder-white/40 focus:outline-none focus:border-blue-accent transition-all duration-300`}
            autoFocus
          />
          {error && (
            <p className="text-red-400 text-sm mt-2">Incorrect password</p>
          )}
          <button
            type="submit"
            className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-blue-accent to-light-blue text-white font-medium hover:shadow-xl hover:shadow-blue-accent/30 transition-all duration-300"
          >
            Enter Site
          </button>
        </form>
      </div>
    </div>
  );
}
// ========== END PASSWORD PROTECTION ==========

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  return (
    <LanguageProvider>
    <PasswordGate>
    <Router>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-lg shadow-lg shadow-navy-primary/5'
            : 'bg-transparent'
        }`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex justify-between items-center">
            <Link to="/" className="logo relative z-50">
              <img
                src="/logo.png"
                alt="VentuNext"
                className={`h-6 transition-all duration-300 ${!isScrolled && !isMobileMenuOpen ? 'brightness-0 invert' : ''}`}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <Navigation isScrolled={isScrolled} />
              <LanguageButton isScrolled={isScrolled} isMobileMenuOpen={isMobileMenuOpen} />
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-4">
              <LanguageButton isScrolled={isScrolled} isMobileMenuOpen={isMobileMenuOpen} />
              <button
                className="relative z-50 w-10 h-10 flex flex-col items-center justify-center gap-1.5"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <span className={`w-6 h-0.5 transition-all duration-300 ${
                  isMobileMenuOpen
                    ? 'bg-navy-primary rotate-45 translate-y-2'
                    : isScrolled ? 'bg-navy-primary' : 'bg-white'
                }`}></span>
                <span className={`w-6 h-0.5 transition-all duration-300 ${
                  isMobileMenuOpen
                    ? 'opacity-0'
                    : isScrolled ? 'bg-navy-primary' : 'bg-white'
                }`}></span>
                <span className={`w-6 h-0.5 transition-all duration-300 ${
                  isMobileMenuOpen
                    ? 'bg-navy-primary -rotate-45 -translate-y-2'
                    : isScrolled ? 'bg-navy-primary' : 'bg-white'
                }`}></span>
              </button>
            </div>
          </div>

        </header>

        {/* Mobile Menu Overlay - moved outside header for proper z-index stacking */}
        <div className={`lg:hidden fixed inset-0 bg-white z-40 transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}>
          <div className={`h-full flex flex-col pt-24 px-8 transition-all duration-500 delay-100 ${
            isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}>
            <Navigation isMobile onClose={() => setIsMobileMenuOpen(false)} />

            <div className="mt-auto pb-8">
              <div className="pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-500">Contact us at</p>
                <a href="mailto:info@ventunext.com" className="text-blue-accent font-medium">
                  info@ventunext.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/business" element={<Business />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/team" element={<Team />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/career" element={<Career />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
    </PasswordGate>
    </LanguageProvider>
  );
}

export default App;
